// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback } from 'react';
import { usePlaidConnection } from '../../hooks/use-plaid-connection';
import { useWealthReaderConnection } from '../../hooks/use-wealth-reader-connection';
import { selectClientInstitution } from '@shared/data-access/slices/clients';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Tooltip } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';

export interface ReconnectAccountButtonProps {
  className?: string;
  institution?: string;
  messageType?: 'short' | 'long';
  asButton?: boolean;
  fullWidth?: boolean;
}

export const ReconnectAccountButton = ({
  className,
  institution: institutionId,
  messageType = 'long',
  asButton = false,
  fullWidth = false,
}: ReconnectAccountButtonProps) => {
  const { t } = useTranslation();
  const institution = useSelector((state: any) =>
    selectClientInstitution(state, institutionId),
  );
  const { handleWealthReaderConnection } = useWealthReaderConnection();
  const { handleLinkAccount } = usePlaidConnection();

  const handleReconnect = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      /* istanbul ignore if */
      if (institution?.provider === 'wealth_reader') {
        handleWealthReaderConnection({
          entitiesToDisplay: institution ? [institution.institution_id] : [],
          defaultLogin: institution?.institution_id,
        });
      }

      if (institution?.provider === 'plaid') {
        handleLinkAccount();
      }
    },
    [handleLinkAccount, handleWealthReaderConnection, institution],
  );

  const messageKey =
    messageType === 'short'
      ? 'aggregator.reconnect-short'
      : 'aggregator.reconnect-account';

  return (
    <Tooltip title={t(messageKey, { ns: 'aggregator' })}>
      {asButton ? (
        <Button
          variant="contained"
          color="secondary"
          fullWidth={fullWidth}
          className={className}
          onClick={handleReconnect}
          startIcon={<AppIcon icon="RefreshTwoArrows" color="inherit" size="lg" />}
        >
          {t(messageKey, { ns: 'aggregator' })}
        </Button>
      ) : (
        <span className={className} onClick={handleReconnect} role="button">
          {t(messageKey, { ns: 'aggregator' })}
        </span>
      )}
    </Tooltip>
  );
};
